import { useMutation } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { AppLoader } from '~/components/loader'
import { useCurrentUser, useTimespanPreference } from '~/hooks'
import { useCurrentUserSetting } from '~/hooks/useCurrentUserSetting'
import { useTRPC } from '~/services/trpc'

import {
  AnalyticsQueryAggregate,
  AnalyticsQueryFilter,
  AnalyticsQueryOrder,
  LookupFields,
} from '@hap/analytics'
import { Timespan } from '@hap/utils'
import { createNanoId } from '@hap/utils'

import {
  DEFAULT_INITIAL_VALUE,
  LOAD_QUERY,
  SET_AGGREGATIONS,
  SET_COLUMNS,
  SET_FIELDS,
  SET_FILTERS,
  SET_ORDER,
  SET_ROWS,
  SET_TASK,
  SET_TIMESPAN,
} from './constants'
import { SavedQueryContext } from './context'
import { useSavedQueryInitializer } from './hooks/use-default-query-params'
import { initializeReducer } from './reducer'
import {
  LoadQueryAction,
  PivotField,
  QueryTask,
  SavedQueryState,
  SavedQueryType,
} from './types'

export function SavedQueryProvider({
  initialValue,
  type,
  children,
  from,
}: {
  children?: React.ReactNode
  initialValue?: SavedQueryState['data']['params']
  type?: SavedQueryType
  from: '/$alias/explore/' | '/$alias/reports/pivot/'
}) {
  const trpc = useTRPC()
  const currentUser = useCurrentUser()
  const { timespan: defaultDateRange, setTimespanPreference } = useTimespanPreference()
  const defaultInitialValue = {
    ...DEFAULT_INITIAL_VALUE,
    relativeTimespan: defaultDateRange,
    timespan: Timespan.toStaticTimespan(defaultDateRange),
  }
  const mutator = useMutation(trpc.queries.upsert.mutationOptions())

  const [defaultId] = useState(createNanoId())
  const [limit] = useCurrentUserSetting('lookup', 'pageSize', 100)
  const searchParams = useSearch({ from: '/_authenticated/$alias/_app' })
  const navigate = useNavigate({ from: from })
  const [qid, setQid] = useState<string>(() => searchParams.qid || defaultId)

  const reducer = useMemo(() => {
    return initializeReducer({ qid, mutator, currentUserId: currentUser?.id })
  }, [qid])

  const [state, dispatch] = useReducer(reducer, {
    meta: {
      isDirty: false,
      isOwner: true,
      isSaved: false,
    },
    relationships: {
      task: null,
    },
    data: { params: initialValue ?? defaultInitialValue },
  })

  const [ready] = useSavedQueryInitializer(
    qid,
    initialValue ?? defaultInitialValue,
    dispatch,
    type,
  )
  const searchQId = searchParams.qid
  useEffect(() => {
    if (qid !== searchQId && searchQId) {
      setQid(searchQId)
      navigate({
        search: (prevSearch) => ({
          ...prevSearch,
          qid: searchQId,
        }),
        replace: true,
      })
    }

    if (!searchQId) {
      setQid(defaultId)
      navigate({
        search: (prevSearch) => ({
          ...prevSearch,
          qid: defaultId,
        }),
        replace: true,
      })
    }
  }, [searchQId, qid])

  const setPage = useCallback((page: number, replace = false) => {
    navigate({
      search: (prevSearch) => ({ ...prevSearch, p: page }),
      replace,
      resetScroll: true,
    })
    window.scrollTo(0, 0)
  }, [])

  const load = useCallback(
    async (data: LoadQueryAction['payload']) => {
      dispatch({ type: LOAD_QUERY, payload: data })
      setPage(1, true)
    },
    [dispatch, setPage],
  )

  const setTimespan = useCallback(
    (timespan: Timespan.Info) => {
      setTimespanPreference(timespan)
      dispatch({ type: SET_TIMESPAN, payload: timespan })
      setPage(1, true)
    },
    [dispatch, setPage, setTimespanPreference],
  )

  const setFilters = useCallback(
    (filters: AnalyticsQueryFilter[]) => {
      dispatch({ type: SET_FILTERS, payload: filters })
      setPage(1, true)
    },
    [dispatch, setPage],
  )

  const setOrder = useCallback(
    (order: AnalyticsQueryOrder[]) => {
      dispatch({ type: SET_ORDER, payload: order })
    },
    [dispatch],
  )

  const setColumns = useCallback(
    (columns: PivotField[]) => {
      dispatch({ type: SET_COLUMNS, payload: columns })
    },
    [dispatch],
  )

  const setRows = useCallback(
    (rows: PivotField[]) => {
      dispatch({ type: SET_ROWS, payload: rows })
    },
    [dispatch],
  )

  const setAggregations = useCallback(
    (aggregations: AnalyticsQueryAggregate[]) => {
      dispatch({ type: SET_AGGREGATIONS, payload: aggregations })
    },
    [dispatch],
  )

  const setTask = useCallback(
    (task: QueryTask) => {
      dispatch({ type: SET_TASK, payload: task })
    },
    [dispatch],
  )

  const setFields = useCallback(
    (fields: LookupFields[]) => {
      dispatch({ type: SET_FIELDS, payload: fields })
    },
    [dispatch],
  )

  return (
    <SavedQueryContext.Provider
      value={{
        id: qid,
        state: state,
        pagination: {
          page: searchParams.p || 1,
          size: limit,
        },
        actions: {
          setTimespan,
          setFilters,
          setOrder,
          setColumns,
          setRows,
          setAggregations,
          setTask,
          setFields,
          setPage,
          load,
        },
      }}
    >
      {ready ? children : <AppLoader />}
    </SavedQueryContext.Provider>
  )
}
